import { useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type { Contact } from '@/lib/types';
import {
  Badge,
  Button,
  Combobox,
  type ComboboxOption,
  Field,
  Modal,
  SegmentedControl,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

/*
 * Previews a template exactly as `POST /marketing/templates/render-preview`
 * renders it: variables resolved, header/footer blocks composed, and the locked
 * compliance footer appended — but with NO tracking.
 *
 * ⛔ **The rendered HTML goes into a sandboxed `srcdoc` iframe with an EMPTY
 * `sandbox` attribute**, so no script in an imported template can run in this
 * origin. Never `dangerouslySetInnerHTML` it.
 *
 * The HTML is asked for from the CALLER (`getHtml`) at the moment the dialog
 * opens, because the editor's current content lives inside the builder and is
 * only reachable through its handle.
 */

export interface TemplatePreviewModalProps {
  open: boolean;
  onClose: () => void;
  /** Resolves to the template body as it stands in the editor right now. */
  getHtml: () => Promise<string>;
  subject: string;
  preheader: string;
  headerBlockId: number | null;
  footerBlockId: number | null;
}

type Device = 'desktop' | 'mobile';
type View = 'html' | 'text';

interface PreviewResult {
  subject: string;
  html: string;
  text: string;
  unresolved: string[];
}

const DEVICE_OPTIONS = [
  { value: 'desktop' as const, label: 'Desktop' },
  { value: 'mobile' as const, label: 'Mobile' },
];

const VIEW_OPTIONS = [
  { value: 'html' as const, label: 'HTML' },
  { value: 'text' as const, label: 'Plain text' },
];

function contactLabel(contact: Contact): string {
  const name = contact.name.trim();
  if (name && contact.email) return `${name} · ${contact.email}`;
  return name || contact.email || `Contact #${contact.id}`;
}

export function TemplatePreviewModal({
  open,
  onClose,
  getHtml,
  subject,
  preheader,
  headerBlockId,
  footerBlockId,
}: TemplatePreviewModalProps) {
  const [contactId, setContactId] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [contactOptions, setContactOptions] = useState<ComboboxOption[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const [device, setDevice] = useState<Device>('desktop');
  const [view, setView] = useState<View>('html');

  const [result, setResult] = useState<PreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Reset when reopened, so a stale preview never stands in for the new one.
  useEffect(() => {
    if (!open) return;
    setResult(null);
    setError(null);
  }, [open]);

  // Contact search — debounced, aborted, and only while the dialog is open.
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setContactsLoading(true);
      api
        .listContacts(
          { page: 1, perPage: 20, ...(contactSearch.trim() ? { search: contactSearch.trim() } : {}) },
          controller.signal,
        )
        .then((page) => {
          setContactOptions(
            page.data.map((contact) => ({
              value: String(contact.id),
              label: contactLabel(contact),
              ...(contact.company ? { hint: contact.company.name } : {}),
            })),
          );
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setContactOptions([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setContactsLoading(false);
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, contactSearch]);

  // Render the preview whenever it opens or the sample contact changes.
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const html = await getHtml();
        if (controller.signal.aborted) return;
        // A POST taking no signal: a superseded render is discarded on arrival
        // by the `aborted` check below rather than cancelled in flight.
        const response = await api.renderMarketingPreview({
          html,
          ...(subject.trim() ? { subject } : {}),
          ...(preheader.trim() ? { preheader } : {}),
          ...(headerBlockId !== null ? { headerBlockId } : {}),
          ...(footerBlockId !== null ? { footerBlockId } : {}),
          ...(contactId ? { contactId: Number(contactId) } : {}),
        });
        if (controller.signal.aborted) return;
        setResult(response.data);
      } catch (caught) {
        if (controller.signal.aborted) return;
        setResult(null);
        setError(
          caught instanceof ApiError
            ? (caught.formMessage ?? caught.message)
            : caught instanceof Error
              ? caught.message
              : 'The preview could not be rendered.',
        );
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
    // getHtml is read at open time; re-rendering on every parent render would
    // re-run the builder export for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contactId]);

  return (
    <Modal open={open} onClose={onClose} title="Preview" size="xl">
      <div className="grid min-w-0 gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full min-w-0 sm:w-80">
            <Field label="Preview as contact">
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  value={contactId}
                  onChange={setContactId}
                  options={contactOptions}
                  emptyOptionLabel="Sample values"
                  placeholder="Sample values"
                  searchPlaceholder="Search contacts…"
                  onSearch={setContactSearch}
                  loading={contactsLoading}
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          </div>
          <div className="w-full sm:w-56">
            <SegmentedControl label="Preview format" value={view} options={VIEW_OPTIONS} onChange={setView} size="sm" />
          </div>
          {view === 'html' && (
            <div className="w-full sm:w-56">
              <SegmentedControl label="Preview width" value={device} options={DEVICE_OPTIONS} onChange={setDevice} size="sm" />
            </div>
          )}
        </div>

        {error && (
          <Notice tone="danger" role="alert" title="The preview could not be rendered.">
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        {loading && !result && (
          <div className="grid gap-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-96 w-full" />
          </div>
        )}

        {result && (
          <div className="grid min-w-0 gap-3">
            <div className="min-w-0 rounded-md border border-border bg-surface-sunken px-3 py-2">
              <p className="m-0 text-xs text-fg-muted">Subject</p>
              <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                {result.subject || <span className="text-fg-subtle">No subject</span>}
              </p>
            </div>

            {result.unresolved.length > 0 && (
              <Notice
                tone="warning"
                role="status"
                title={
                  result.unresolved.length === 1
                    ? '1 variable has no value for this recipient and renders empty'
                    : `${result.unresolved.length} variables have no value for this recipient and render empty`
                }
              >
                <div className="mt-1 flex flex-wrap gap-1">
                  {result.unresolved.map((key) => (
                    <Badge key={key} tone="warning" variant="outline">
                      {`{{ ${key} }}`}
                    </Badge>
                  ))}
                </div>
              </Notice>
            )}

            <div className={loading ? 'opacity-60 transition-opacity duration-150 ease-out motion-reduce:transition-none' : undefined}>
              {view === 'html' ? (
                <div className="flex justify-center rounded-md border border-border bg-surface-sunken p-2 sm:p-4">
                  <iframe
                    title="Email preview"
                    sandbox=""
                    srcDoc={result.html}
                    className="h-[60vh] min-h-96 w-full max-w-full rounded-sm border border-border bg-white"
                    style={device === 'mobile' ? { width: 375 } : undefined}
                  />
                </div>
              ) : (
                <pre className="m-0 max-h-[60vh] min-h-96 overflow-auto rounded-md border border-border bg-surface-sunken p-3 text-xs whitespace-pre-wrap text-fg [overflow-wrap:anywhere]">
                  {result.text}
                </pre>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
